import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  MessageSquare, Send, AlertTriangle, CheckCircle2, Loader2, Euro, Activity, Clock,
} from "lucide-react";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { useSearchParams } from "react-router-dom";
import { SmsLogTable, FilterPills, STATUS_FILTERS, KIND_FILTERS, PURPOSE_DE } from "@/components/sms/SmsLogTable";

const RANGES = [
  { value: 7,  label: "7 Tage" },
  { value: 30, label: "30 Tage" },
  { value: 90, label: "90 Tage" },
  { value: 0,  label: "Gesamt" },
];

const _num = (v) => Number(v || 0).toLocaleString("de-DE");
const _eur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });
const _pct = (v) => `${Number(v || 0).toFixed(1)} %`;

export default function AdminSmsStats() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recent, setRecent] = useState([]);
  const [params] = useSearchParams();
  const [recentFilter, setRecentFilter] = useState(params.get("status") || "all");
  const [kind, setKind] = useState("all");
  const [recentLoading, setRecentLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [running, setRunning] = useState(false);

  const loadAlerts = async () => {
    try {
      const { data } = await api.get(`/admin/sms-alerts?limit=10`);
      setAlerts(data || []);
    } catch { /* non-critical */ }
  };

  const runAnomalyCheck = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/admin/sms-alerts/check`);
      if (data?.triggered) {
        toast.error(`Anomalie erkannt: −${data.drop_pct}pp Zustellrate · ${data.push_deliveries} Push gesendet`);
      } else {
        toast.success("Kein Einbruch erkannt");
      }
      await loadAlerts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anomalie-Check fehlgeschlagen");
    } finally {
      setRunning(false);
    }
  };

  const loadStats = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/sms-stats?days=${days}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte SMS-Statistik nicht laden");
    } finally {
      setLoading(false);
    }
  };

  const loadRecent = async () => {
    setRecentLoading(true);
    try {
      const { data } = await api.get("/admin/sms-stats/recent", { params: { limit: 200, status: recentFilter, kind, days } });
      setRecent(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte SMS-Log nicht laden");
    } finally {
      setRecentLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadStats(); }, [days]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadRecent(); }, [recentFilter, kind, days]);
  useEffect(() => { loadAlerts(); }, []);

  const k = data?.kpis || {};
  const timeline = data?.timeline || [];
  const tlMax = Math.max(1, ...timeline.map((t) => t.ok + t.failed));
  const topErr = data?.top_errors || [];
  const purposes = Object.entries(Object.entries(data?.purpose_counts || {}).reduce((acc, [k, n]) => {
    const l = PURPOSE_DE[k] || k;
    acc[l] = (acc[l] || 0) + n;
    return acc;
  }, {})).sort((a, b) => b[1] - a[1]);

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-sms-stats-page">
      <AppHeader title="SMS-Versandstatistik" back="/dashboard" />
      <main className="max-w-6xl mx-auto px-3 sm:px-5 py-5 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <MessageSquare className="text-sky-400" size={22} />
            <div>
              <h1 className="font-display text-xl sm:text-2xl font-bold">SMS-Versandstatistik</h1>
              <p className="text-[12px] text-zinc-300 font-medium">Welche SMS angekommen sind und welche nicht – mit Grund</p>
            </div>
          </div>
          <div className="flex rounded-xl bg-[#18181B] border border-zinc-800 overflow-hidden">
            {RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => setDays(r.value)}
                data-testid={`sms-range-${r.value}`}
                className={`px-3 h-9 text-[12px] font-bold uppercase tracking-wider ${days === r.value ? "bg-[#00E575] text-[#09090B]" : "text-zinc-400 hover:bg-zinc-800"}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-400 inline-flex w-full justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> lädt…
          </div>
        )}

        {!loading && data && (
          <>
            {/* Anomaly Alerts */}
            {alerts.length > 0 && (
              <section className="bg-rose-500/10 border border-rose-500/40 rounded-2xl p-4" data-testid="sms-anomaly-alerts">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="text-rose-300 mt-0.5 shrink-0" size={18} />
                  <div className="flex-1 min-w-0">
                    <h2 className="font-display text-sm font-bold text-rose-200">Anomalie-Verlauf</h2>
                    <ul className="mt-2 space-y-1 text-[12px] text-rose-100/90 font-medium">
                      {alerts.slice(0, 3).map((a) => (
                        <li key={a.id} className="flex flex-wrap items-baseline gap-x-2" data-testid={`sms-alert-${a.id}`}>
                          <span className="font-mono text-[12px] text-rose-300/80">
                            {a.created_at ? new Date(a.created_at).toLocaleString("de-DE") : "—"}
                          </span>
                          <span>Zustellrate {a.rate_24h_pct}% (Norm {a.rate_baseline_pct}%) — −{a.drop_pct}pp · {a.push_deliveries || 0} Push gesendet</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>
            )}

            <div className="flex justify-end">
              <button
                onClick={runAnomalyCheck}
                disabled={running}
                data-testid="sms-anomaly-run-check"
                className="h-8 px-3 rounded-lg bg-[#18181B] border border-zinc-800 text-[12px] font-bold uppercase tracking-wider text-zinc-300 hover:bg-zinc-800 inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                {running ? <Loader2 size={12} className="animate-spin" /> : <Activity size={12} />}
                Anomalie jetzt prüfen
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard icon={Send} label="Versendet" value={_num(k.total)} hint={`${_num(k.ok)} angenommen`} testid="sms-kpi-total" />
              <KpiCard icon={CheckCircle2} label="Zustellrate" value={_pct(k.delivery_rate_pct)} hint={k.delivered != null ? `${_num(k.delivered)} bestätigt zugestellt` : "Webhook-Daten ausstehend"} accent="green" testid="sms-kpi-delivery" />
              <KpiCard icon={Activity} label="Annahmequote" value={_pct(k.success_rate_pct)} hint={k.failed ? `${_num(k.failed)} Fehler` : "Alle OK"} accent="sky" testid="sms-kpi-success" />
              <KpiCard icon={AlertTriangle} label="Fehlgeschlagen" value={_num(k.failed)} hint={k.invalid_phone > 0 ? `${k.invalid_phone} ungültige Nummern` : "—"} accent={k.failed > 0 ? "rose" : null} testid="sms-kpi-failed" />
              <KpiCard icon={Euro} label="Geschätzte Kosten" value={_eur(k.estimated_cost_eur)} hint="≈ 0,075 € pro SMS" testid="sms-kpi-cost" />
            </div>

            {/* Timeline */}
            <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5" data-testid="sms-stats-timeline">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-lg font-bold">Verlauf · letzte {timeline.length} Tage</h2>
                <div className="flex gap-3 text-[12px]">
                  <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 bg-[#00E575] rounded-sm" /> Erfolgreich</span>
                  <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 bg-rose-400 rounded-sm" /> Fehler</span>
                </div>
              </div>
              {timeline.length === 0 ? (
                <div className="text-zinc-500 text-sm text-center py-6">Keine Daten</div>
              ) : (
                <div className="flex items-end gap-1 h-40">
                  {timeline.map((t, i) => {
                    const totalDay = t.ok + t.failed;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative">
                        <div className="w-full flex flex-col-reverse h-32 rounded-t-sm overflow-hidden bg-zinc-900/40">
                          <div className="w-full bg-[#00E575]/80" style={{ height: `${(t.ok / tlMax) * 100}%` }} title={`${t.date}: ${t.ok} erfolgreich`} />
                          <div className="w-full bg-rose-400/80" style={{ height: `${(t.failed / tlMax) * 100}%` }} title={`${t.date}: ${t.failed} Fehler`} />
                        </div>
                        <div className="text-[8px] text-zinc-500 -rotate-45 origin-top-left translate-y-2 whitespace-nowrap">{t.date.slice(5)}</div>
                        {totalDay > 0 && (
                          <span className="absolute -top-5 left-1/2 -translate-x-1/2 hidden group-hover:block text-[12px] bg-zinc-900 border border-zinc-700 rounded px-1 whitespace-nowrap">{totalDay}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Top Errors + Purposes */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5" data-testid="sms-top-errors">
                <h2 className="font-display text-lg font-bold mb-3 inline-flex items-center gap-2"><AlertTriangle size={16} className="text-rose-400" /> Häufigste Fehler</h2>
                {topErr.length === 0 ? (
                  <div className="text-zinc-500 text-sm py-3">Keine Fehler im Zeitraum 🎉</div>
                ) : (
                  <ul className="space-y-2 text-[13px]">
                    {topErr.map((e, i) => (
                      <li key={i} className="flex items-center justify-between border-b border-zinc-800/50 last:border-0 pb-2 last:pb-0" data-testid={`sms-error-row-${i}`}>
                        <span className="text-zinc-200 truncate max-w-[70%]" title={e.error}>{e.error}</span>
                        <span className="font-mono text-rose-300 font-bold">{_num(e.count)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5" data-testid="sms-purposes">
                <h2 className="font-display text-lg font-bold mb-3 inline-flex items-center gap-2"><Activity size={16} className="text-sky-400" /> Versand-Anlass</h2>
                {purposes.length === 0 ? (
                  <div className="text-zinc-500 text-sm py-3">—</div>
                ) : (
                  <ul className="space-y-2 text-[13px]">
                    {purposes.map(([name, count]) => (
                      <li key={name} className="flex items-center justify-between border-b border-zinc-800/50 last:border-0 pb-2 last:pb-0">
                        <span className="text-zinc-300">{name}</span>
                        <span className="font-mono text-sky-300 font-bold">{_num(count)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            {/* Recent Log */}
            <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5" data-testid="sms-recent-log">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <h2 className="font-display text-lg font-bold inline-flex items-center gap-2"><Clock size={16} className="text-zinc-300" /> Alle SMS im Zeitraum</h2>
                <div className="flex flex-wrap gap-2">
                  <FilterPills items={STATUS_FILTERS} value={recentFilter} onChange={setRecentFilter} testid="sms-recent-filter" />
                  <FilterPills items={KIND_FILTERS} value={kind} onChange={setKind} testid="sms-kind-filter" />
                </div>
              </div>
              {recentLoading ? (
                <div className="text-zinc-500 text-sm py-6 text-center"><Loader2 size={14} className="animate-spin inline mr-1" /> lädt…</div>
              ) : recent.length === 0 ? (
                <div className="text-zinc-500 text-sm py-6 text-center" data-testid="sms-recent-empty">Keine Einträge</div>
              ) : (
                <SmsLogTable rows={recent} onReload={loadRecent} />
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, hint, accent, testid }) {
  const accentColor =
    accent === "green" ? "text-[#00E575]" :
    accent === "rose"  ? "text-rose-300"  :
    accent === "amber" ? "text-amber-300" :
    "text-zinc-100";
  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold">{label}</span>
        <Icon className={accentColor} size={14} />
      </div>
      <div className={`font-display text-2xl font-bold ${accentColor}`}>{value ?? "—"}</div>
      {hint && <div className="text-[12px] text-zinc-300 mt-1">{hint}</div>}
    </div>
  );
}
